import * as React from "react";
import {
	Box,
	Card,
	Divider,
	FormControlLabel,
	Grid,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr/CheckCircle";

import type { CampaignRequestDocument } from "@/hooks/api/use-campaign-requests";
import { useUpdateCampaignRequest } from "@/hooks/api/use-campaign-requests";

import { DocumentRows, DocumentUploadButton } from "../components/document-list";
import { formatDate, marketFlag, marketLabel } from "../constants";
import { sameTime } from "./dirty";
import { MARKET_DISCLOSURES } from "./reference";
import type { StepProps } from "./wizard-shell";
import { StepHeading, useWizardDirty, WizardFooter } from "./wizard-shell";

/**
 * Step 6 — compliance. Prefilled from the account, editable per campaign.
 *
 * The do-not-call register is stated, not offered. It is account-level in the platform and applies
 * across every market automatically, so there is nothing here to switch — and saying it plainly is
 * what stops someone assuming they have to re-upload it per market.
 *
 * The per-market disclosure table is reference, not input. We maintain it — it turns a compliance
 * question the client would have to research into something they just read.
 */

/** `HH:MM:SS` off Postgres, `HH:MM` into an `<input type="time">`. */
function toTimeInput(value: string | null | undefined): string {
	return value ? value.slice(0, 5) : "";
}

/**
 * The newest suppression list, chosen by its own timestamp and never by its position.
 *
 * This used to be `dncDocuments.at(-1)`, under a comment saying the most recent applies. The API
 * hands documents back newest first, so `.at(-1)` was the *oldest* — the superseded list — and a
 * client who re-uploaded after adding numbers had those numbers dialled anyway. Nothing failed,
 * nothing was logged, and the only symptom was a call to somebody who had asked not to be called.
 *
 * Comparing `createdAt` cannot invert if that ordering ever changes again, which is the whole
 * reason it is stated rather than assumed. Ties keep the earlier of the two only because one of
 * them has to win; two lists uploaded inside the same second are a client problem, not a rule.
 */
function newestByUpload(documents: CampaignRequestDocument[]): CampaignRequestDocument | null {
	return documents.reduce<CampaignRequestDocument | null>((newest, document) => {
		if (!newest) return document;
		return Date.parse(document.createdAt) > Date.parse(newest.createdAt) ? document : newest;
	}, null);
}

export function StepCompliance({ request, goTo, exit, readOnly }: StepProps): React.JSX.Element {
	const updateMutation = useUpdateCampaignRequest();

	const [recording, setRecording] = React.useState(request.recordingDisclosure);
	const [aiDisclosure, setAiDisclosure] = React.useState(request.aiDisclosure);
	const [transferEnabled, setTransferEnabled] = React.useState(Boolean(request.transferNumber));
	const [transferNumber, setTransferNumber] = React.useState(request.transferNumber ?? "");
	const [windowStart, setWindowStart] = React.useState(toTimeInput(request.transferWindowStart) || "09:00");
	const [windowEnd, setWindowEnd] = React.useState(toTimeInput(request.transferWindowEnd) || "18:00");

	const dncLists = request.documents.filter((document) => document.kind === "dnc_list");
	const newestDnc = newestByUpload(dncLists);
	const relevant = MARKET_DISCLOSURES.filter((row) => (request.markets ?? []).includes(row.market));
	const unlisted = (request.markets ?? []).filter((market) => !MARKET_DISCLOSURES.some((row) => row.market === market));

	const payload = {
		recordingDisclosure: recording,
		aiDisclosure,
		transferNumber: transferEnabled ? transferNumber.trim() || null : null,
		transferWindowStart: transferEnabled ? windowStart || null : null,
		transferWindowEnd: transferEnabled ? windowEnd || null : null,
		// The most recent suppression list uploaded here is the one that applies, and it
		// applies to every market at once rather than per campaign. Picked by upload time —
		// see `newestByUpload`, and never by position in the array.
		dncListDocumentId: newestDnc?.id ?? request.dncListDocumentId ?? null,
	};

	/**
	 * Whether anything on this step has moved.
	 *
	 * It decides two things and it has to be the same answer for both: whether the save writes at
	 * all — a PUT that changes nothing still runs `recompute()` on the API, which withdraws a live
	 * quote and sends the request back for re-pricing, and this step is prefilled from the account
	 * so passing straight through it is the *normal* path — and whether jumping the rail is about
	 * to throw an edit away.
	 */
	const dirty =
		payload.recordingDisclosure !== request.recordingDisclosure ||
		payload.aiDisclosure !== request.aiDisclosure ||
		payload.transferNumber !== request.transferNumber ||
		!sameTime(payload.transferWindowStart ?? "", request.transferWindowStart) ||
		!sameTime(payload.transferWindowEnd ?? "", request.transferWindowEnd) ||
		payload.dncListDocumentId !== request.dncListDocumentId;

	useWizardDirty(dirty);

	const save = async (): Promise<boolean> => {
		if (!dirty) return true;

		try {
			await updateMutation.mutateAsync({
				id: request.id,
				data: payload,
			});
			return true;
		} catch {
			return false;
		}
	};

	const handleContinue = async (): Promise<void> => {
		if (await save()) goTo(7);
	};

	const handleSaveExit = async (): Promise<void> => {
		if (await save()) exit();
	};

	return (
		<Stack spacing={3}>
			<StepHeading
				title="Rules for these calls"
				help="Carried over from your account settings. Change anything that's specific to this campaign."
			/>

			{/*
			 * One card, because it is one subject: who we will not call.
			 *
			 * Ours is a fact — account-level, every market, always — and theirs is a file. They were
			 * two cards, which read as two settings and left the client wondering whether the upload
			 * turned something on.
			 *
			 * So ours is written as a fact rather than as a control. It was a switch that was checked,
			 * disabled and pilled "Always on": three ways of saying one thing, one of them a toggle
			 * that answered a click by doing nothing and a pill that only restated the line beside it.
			 */}
			<Card variant="outlined" sx={{ p: 2 }}>
				<Stack spacing={2}>
					<Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
						<CheckCircleIcon
							color="var(--mui-palette-success-main)"
							fontSize="var(--icon-fontSize-md)"
							weight="fill"
							style={{ flex: "0 0 auto", marginTop: 2 }}
						/>
						<Stack spacing={0.25}>
							<Typography variant="subtitle2">We honour our do-not-call register on every call</Typography>
							<Typography variant="body2" color="text.secondary">
								Held on your account and applied to every market.
							</Typography>
						</Stack>
					</Stack>

					<Divider />

					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={1}
						sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
					>
						<Typography variant="subtitle2">Your Own Suppression List</Typography>
						{/* Named for what it is here — "do-not-call list" is the register above, which is
						    ours and is not uploaded. */}
						{readOnly ? null : (
							<DocumentUploadButton requestId={request.id} kind="dnc_list" label="Upload suppression list" />
						)}
					</Stack>

					{dncLists.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							CSV UTF-8 only, one number per row. Export Excel sheets as CSV before uploading.
						</Typography>
					) : (
						<DocumentRows requestId={request.id} documents={dncLists} readOnly={readOnly} showKind={false} />
					)}

					{/* Which file we will actually load, named — and only where there is more than one to
					    choose between. A client who uploads a second, longer list has no other way to
					    tell whether it superseded the first, and the failure if it did not is a call to
					    somebody who asked us not to call them. */}
					{dncLists.length > 1 && newestDnc ? (
						<Typography variant="caption" color="text.secondary">
							{`We use the newest: ${newestDnc.originalFilename}, uploaded ${formatDate(newestDnc.createdAt)}.`}
						</Typography>
					) : null}
				</Stack>
			</Card>

			{/*
			 * One card for the three things that are true of every call on this campaign.
			 *
			 * They used to be split across two, with "speak to a person" alone in a full-width card
			 * and the two announcements sharing the one under it — a grouping that says a difference
			 * exists where there is none. They are the same kind of decision, made in the same place,
			 * and each carries what it means underneath it rather than behind a hover.
			 */}
			<Card variant="outlined" sx={{ p: 2 }}>
				<Stack spacing={2.5}>
					{/* No subtitle: "applied across every market" is already said twice above, and
					    "On every call" is the whole of what this card means. */}
					<Typography variant="subtitle1">On Every Call</Typography>

					<Stack spacing={0.5}>
						<FormControlLabel
							control={
								<Switch
									checked={transferEnabled}
									disabled={readOnly}
									onChange={(event) => setTransferEnabled(event.target.checked)}
								/>
							}
							label="Let callers ask to speak to a person"
						/>
						{transferEnabled ? (
							<Grid container spacing={2} sx={{ pt: 1 }}>
								<Grid size={{ xs: 12, md: 6 }}>
									<TextField
										fullWidth
										disabled={readOnly}
										label="Transfer to"
										placeholder="+65 6015 2299"
										value={transferNumber}
										onChange={(event) => setTransferNumber(event.target.value)}
									/>
								</Grid>
								<Grid size={{ xs: 6, md: 3 }}>
									<TextField
										fullWidth
										disabled={readOnly}
										type="time"
										label="Available from"
										value={windowStart}
										onChange={(event) => setWindowStart(event.target.value)}
									/>
								</Grid>
								<Grid size={{ xs: 6, md: 3 }}>
									<TextField
										fullWidth
										disabled={readOnly}
										type="time"
										label="Available to"
										value={windowEnd}
										onChange={(event) => setWindowEnd(event.target.value)}
										helperText="Local to the desk answering."
									/>
								</Grid>
							</Grid>
						) : null}
					</Stack>

					<FormControlLabel
						control={
							<Switch
								checked={recording}
								disabled={readOnly}
								onChange={(event) => setRecording(event.target.checked)}
							/>
						}
						label="Announce that the call is recorded"
					/>

					<FormControlLabel
						control={
							<Switch
								checked={aiDisclosure}
								disabled={readOnly}
								onChange={(event) => setAiDisclosure(event.target.checked)}
							/>
						}
						label="Announce that the caller is an AI assistant"
					/>
				</Stack>
			</Card>

			<Card variant="outlined" sx={{ p: 2 }}>
				<Stack spacing={1.5}>
					<Box>
						<Typography variant="subtitle1">Required Disclosures by Market</Typography>
						<Typography variant="body2" color="text.secondary">
							Reference only.
						</Typography>
					</Box>

					{/* Four column headings over nothing is not a table. Where no market has been chosen
					    yet there is nothing to scan, so the sentence stands on its own. */}
					{relevant.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							Choose your markets on step 1 and their requirements appear here.
						</Typography>
					) : (
						<Box sx={{ overflowX: "auto" }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 600 }}>Market</TableCell>
										<TableCell sx={{ fontWeight: 600 }}>Recording Notice</TableCell>
										<TableCell sx={{ fontWeight: 600 }}>AI Disclosure</TableCell>
										<TableCell sx={{ fontWeight: 600 }}>Calling Hours Limit</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{relevant.map((row) => (
										<TableRow key={row.market}>
											<TableCell>
												{marketFlag(row.market)} {marketLabel(row.market)}
											</TableCell>
											<TableCell>{row.recording}</TableCell>
											<TableCell>{row.aiDisclosure}</TableCell>
											<TableCell>{row.callingHours}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>
					)}

					{unlisted.length === 0 ? null : (
						<Typography variant="caption" color="text.secondary">
							Requirements for {unlisted.map((market) => marketLabel(market)).join(", ")} are not published yet.
						</Typography>
					)}
				</Stack>
			</Card>

			<WizardFooter
				busy={updateMutation.isPending}
				onBack={() => goTo(5)}
				onSaveExit={readOnly ? undefined : () => void handleSaveExit()}
				onContinue={readOnly ? () => goTo(7) : () => void handleContinue()}
			/>
		</Stack>
	);
}
